import React, { useEffect, useState } from 'react';
import { X, Swords, UserPlus, Trophy, ChevronRight } from 'lucide-react';

const TOAST_ICONS = {
  duel_invite: { icon: Swords, bg: 'bg-orange-500/20', border: 'border-orange-500/40', text: 'text-orange-400', accent: 'bg-orange-500' },
  friend_request: { icon: UserPlus, bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-400', accent: 'bg-blue-500' },
  achievement: { icon: Trophy, bg: 'bg-yellow-500/20', border: 'border-yellow-500/40', text: 'text-yellow-400', accent: 'bg-yellow-500' },
  default: { icon: Trophy, bg: 'bg-primary/20', border: 'border-primary/40', text: 'text-primary', accent: 'bg-primary' },
};

const ToastItem = ({ toast, onDismiss, onAction }) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  const config = TOAST_ICONS?.[toast?.type] || TOAST_ICONS?.default;
  const IconComponent = config?.icon;

  useEffect(() => {
    const enterTimer = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(enterTimer);
  }, []);

  useEffect(() => {
    if (!toast?.persistent) {
      const autoTimer = setTimeout(() => handleDismiss(), toast?.duration || 6000);
      return () => clearTimeout(autoTimer);
    }
  }, [toast?.id]);

  const handleDismiss = () => {
    setExiting(true);
    setTimeout(() => onDismiss(toast?.id), 300);
  };

  const handleAction = (action) => {
    onAction(toast?.id, action);
    handleDismiss();
  };

  return (
    <div
      className={`
        relative flex items-start gap-3 w-full max-w-sm p-4 rounded-xl
        border backdrop-blur-md shadow-2xl
        ${config?.bg} ${config?.border}
        transition-all duration-300 ease-out
        ${visible && !exiting ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'}
      `}
    >
      {/* Accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${config?.accent}`} />
      {/* Icon */}
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${config?.bg} border ${config?.border}`}>
        <IconComponent className={`w-4 h-4 ${config?.text}`} />
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0 pr-6">
        <p className={`text-sm font-semibold ${config?.text} leading-tight`}>{toast?.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-snug line-clamp-2">{toast?.message}</p>

        {/* Action buttons */}
        {toast?.actions && toast?.actions?.length > 0 && (
          <div className="flex gap-2 mt-2.5">
            {toast?.actions?.map((action, idx) => (
              <button
                key={idx}
                onClick={() => handleAction(action)}
                className={`
                  flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium
                  transition-all duration-150 active:scale-95
                  ${idx === 0
                    ? `${config?.accent} text-white hover:opacity-90`
                    : 'bg-white/10 text-foreground hover:bg-white/20 border border-white/10'
                  }
                `}
              >
                {action?.label}
                {idx === 0 && <ChevronRight className="w-3 h-3" />}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

const ToastNotification = ({ toasts, onDismiss, onAction }) => {
  if (!toasts || toasts?.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none">
      {toasts?.map((toast) => (
        <div key={toast?.id} className="pointer-events-auto">
          <ToastItem
            toast={toast}
            onDismiss={onDismiss}
            onAction={onAction}
          />
        </div>
      ))}
    </div>
  );
};

export default ToastNotification;
